/* The current plan, as the workbook grid - SIM-01..16.
 *
 * Constraint C4 is explicit: "planners know the current grid, so the
 * simulation screen keeps the same sections and row names". So this is not a
 * redesign of the workbook, it is the workbook - the same three sections, the
 * same row labels, the weeks across the columns - with the manual effort
 * removed.
 *
 * Three things the Excel grid cannot do, and the reason this screen exists:
 *   - the DOH cell carries a glyph as well as a colour (SIM-06)
 *   - any computed cell explains how it was obtained (SIM-16)
 *   - every change is undoable: "Revert last apply" restores the plan as it
 *     was before the last simulation was applied (SIM-10)
 *
 * Automatic and manual planning no longer live here: both are simulations
 * (views/Simulations.tsx), saved and compared before anything is applied.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, type CellExplanation, type Grid } from '../api/client'
import { PlanGrid } from '../components/PlanGrid'
import { ExplainPanel } from '../components/ExplainPanel'
import { DohMeter } from '../components/DohMeter'

interface Props {
  valueStreamId: string
  scenario: string
  /* A hidden button is a courtesy, not a control - the API enforces the
     permission regardless. Both exist so a viewer is not shown an action
     they would be refused. */
  canEdit: boolean
  onPlanApplied: () => void
}

export function Simulation({ valueStreamId, scenario, canEdit, onPlanApplied }: Props) {
  const [grid, setGrid] = useState<Grid | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [explain, setExplain] = useState<CellExplanation | null>(null)
  const [lastRecompute, setLastRecompute] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setGrid(await api.grid(valueStreamId, scenario))
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [valueStreamId, scenario])

  useEffect(() => {
    void load()
    setExplain(null)
    setNotice(null)
  }, [load])

  const onEdit = async (crewIndex: number, bucketIndex: number, raw: string) => {
    const days = Number(raw)
    if (!Number.isFinite(days)) return
    try {
      const next = await api.edit(valueStreamId, [{ crew_index: crewIndex, bucket_index: bucketIndex, days }], scenario)
      setGrid(next)
      setLastRecompute(next.recompute?.elapsed_ms ?? null)
      setError(null)
    } catch (e) {
      // SIM-08: a refused value is reported, never silently clamped.
      setError((e as Error).message)
      void load()
    }
  }

  const onRevert = async () => {
    setBusy(true)
    try {
      const next = await api.revertPlan(valueStreamId, scenario)
      setGrid(next)
      setError(null)
      setNotice(`Reverted — ${next.restored_cells ?? 0} cells restored to the previous plan.`)
      onPlanApplied()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (error && !grid) return <div className="card" style={{ padding: 20 }}>{error}</div>
  if (!grid) return <div className="card" style={{ padding: 20 }}>Loading…</div>

  const { buckets, value_stream: vs } = grid
  const dailyCount = buckets.filter((b) => b.type === 'D').length

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <header className="card page-head nested">
        <div className="page-title">
          <h1>{vs.name}</h1>
          <div className="page-sub">
            {vs.project} · {vs.plant} · corridor {vs.min_doh}–{vs.max_doh} days · MPW{' '}
            {vs.mpw.toLocaleString()} ·{' '}
            {vs.mode === 'BY_PIECES' ? 'output by pieces' : 'output by efficiency'}
          </div>
        </div>
        <div className="page-actions">
          <DohMeter value={grid.kpis.average_doh} min={vs.min_doh} max={vs.max_doh} width={150} />
        </div>
      </header>

      <div className="tiles">
        <div className="tile good">
          <div className="tile-label">Weeks in band</div>
          <div className="tile-value">{grid.kpis.weeks_in_band}</div>
        </div>
        <div className={`tile ${grid.kpis.weeks_below_min ? 'critical' : ''}`}>
          <div className="tile-label">Weeks short</div>
          <div className="tile-value">{grid.kpis.weeks_below_min}</div>
        </div>
        <div className={`tile ${grid.kpis.weeks_above_max ? 'warning' : ''}`}>
          <div className="tile-label">Weeks in excess</div>
          <div className="tile-value">{grid.kpis.weeks_above_max}</div>
        </div>
        <div className="tile">
          <div className="tile-label">Average DOH</div>
          <div className="tile-value">{grid.kpis.average_doh}</div>
        </div>
        <div className="tile">
          <div className="tile-label">Overtime days</div>
          <div className="tile-value">{grid.kpis.overtime_days}</div>
        </div>
        <div className="tile">
          <div className="tile-label">Not worked (TLO)</div>
          <div className="tile-value">{grid.kpis.days_not_worked}</div>
        </div>
      </div>

      <div className="card" style={{ padding: '10px 16px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        {!canEdit && <span className="chip neutral">read only for your role</span>}

        {canEdit && grid.can_revert && (
          <button
            onClick={() => void onRevert()}
            disabled={busy}
            title="Restore the plan as it was before the last simulation was applied (SIM-10)"
          >
            ↺ Revert last apply
          </button>
        )}
        <div className="small muted" style={{ marginLeft: 'auto', display: 'flex', gap: 14 }}>
          <span>
            first {dailyCount} buckets are daily, then weekly
          </span>
          {lastRecompute !== null && (
            <span className="mono" title="NFR-02 requires under 1000 ms">
              recompute {lastRecompute.toFixed(1)} ms
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: '10px 14px', borderColor: 'var(--status-critical)', background: 'var(--status-critical-bg)' }}>
          <strong>⚠ Refused.</strong> {error}
        </div>
      )}

      {notice && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '4px solid var(--status-good)' }}>
          <strong>↺</strong> {notice}
        </div>
      )}

      <PlanGrid
        grid={grid}
        editable={canEdit}
        onCellChange={(ci, t, value) => {
          const copy = structuredClone(grid)
          copy.crews[ci].days[t] = value
          setGrid(copy)
        }}
        onCellCommit={(ci, t, raw) => void onEdit(ci, t, raw)}
        onExplain={(t) => void api.explainCell(valueStreamId, t, scenario).then(setExplain)}
      />

      {explain && <ExplainPanel data={explain} onClose={() => setExplain(null)} />}
    </div>
  )
}
